/**
 * Operations Financial API
 */

import { api } from '@/lib/api';
import type {
  FinancialSummary,
  PaymentAging,
  RevenueByType,
  MonthlyRevenue,
  TopAccount,
  TopProject,
  QuoteConversionFunnel,
  QuotesPipelineSummary,
  QuotePipelineItem,
  QuotesPipelineResponse,
  QuotesPipelineQueryParams,
} from './types';

// API Response types (camelCase from backend)
interface ApiInvoiceSummary {
  totalPaid: number;
  totalPending: number;
  totalOverdue: number;
  countPaid: number;
  countPending: number;
  countOverdue: number;
}

interface ApiQuoteSummary {
  pipelineTotal: number;
  countPreparation: number;
  countSent: number;
  countApproved: number;
  countInvoiced: number;
  countRejected: number;
  totalApproved: number;
  totalInvoiced: number;
  conversionRate: number;
}

interface ApiProjectSummary {
  totalCompleted: number;
  totalInProgress: number;
  totalScheduled: number;
  countCompleted: number;
  countInProgress: number;
  countScheduled: number;
}

interface ApiFinancialSummary {
  invoices: ApiInvoiceSummary;
  quotes: ApiQuoteSummary;
  projects: ApiProjectSummary;
}

interface ApiAgingBucket {
  amount: number;
  count: number;
}

interface ApiPaymentAging {
  current: ApiAgingBucket;
  days1to30: ApiAgingBucket;
  days31to60: ApiAgingBucket;
  days61to90: ApiAgingBucket;
  days90Plus: ApiAgingBucket;
}

interface ApiRevenueByType {
  type: string;
  totalAmount: number;
  completedAmount: number;
  totalCount: number;
  completedCount: number;
}

interface ApiMonthlyRevenue {
  month: string;
  invoiceRevenue: number;
  projectRevenue: number;
  invoiceCount: number;
  projectCount: number;
}

interface ApiTopAccount {
  accountId: string;
  accountNumber: string;
  accountName: string;
  totalPaid: number;
  totalPending: number;
  invoiceCount: number;
}

interface ApiTopProject {
  id: string;
  projectNumber: string;
  projectName: string | null;
  type: string;
  status: string;
  amount: number;
  quoteNumber: string | null;
  scheduledDate: string | null;
  completedAt: string | null;
}

interface ApiFunnelStages {
  preparation: number;
  sent: number;
  approved: number;
  invoiced: number;
  rejected: number;
  expired: number;
}

interface ApiFunnelValues {
  total: number;
  approved: number;
  lost: number;
}

interface ApiQuoteConversionFunnel {
  total: number;
  stages: ApiFunnelStages;
  values: ApiFunnelValues;
  conversionRate: number;
  winRate: number;
}

// Quotes Pipeline API types
interface ApiQuotePipelineBucket {
  status: string;
  label: string;
  count: number;
  total: number;
  color: string;
}

interface ApiQuotesPipelineSummary {
  buckets: ApiQuotePipelineBucket[];
  totalQuotes: number;
  totalValue: number;
}

interface ApiQuotePipelineItem {
  id: string;
  quoteNumber: string;
  projectName: string | null;
  poNumber: string | null;
  status: string;
  total: number;
  currency: string;
  accountId: string | null;
  accountName: string | null;
  accountNumber: string | null;
  estimatedDelivery: string | null;
  createdDate: string;
  sentDate: string | null;
  acceptedDate: string | null;
  contractualManagerId: string | null;
  invoicedDate: string | null;
}

interface ApiQuotesPipelineResponse {
  quotes: ApiQuotePipelineItem[];
  pageNumber: number;
  pageLength: number;
  total: number;
}

// Transform functions
function transformSummary(data: ApiFinancialSummary): FinancialSummary {
  return {
    invoices: {
      total_paid: data.invoices.totalPaid,
      total_pending: data.invoices.totalPending,
      total_overdue: data.invoices.totalOverdue,
      count_paid: data.invoices.countPaid,
      count_pending: data.invoices.countPending,
      count_overdue: data.invoices.countOverdue,
    },
    quotes: {
      pipeline_total: data.quotes.pipelineTotal,
      count_preparation: data.quotes.countPreparation,
      count_sent: data.quotes.countSent,
      count_approved: data.quotes.countApproved,
      count_invoiced: data.quotes.countInvoiced,
      count_rejected: data.quotes.countRejected,
      total_approved: data.quotes.totalApproved,
      total_invoiced: data.quotes.totalInvoiced,
      conversion_rate: data.quotes.conversionRate,
    },
    projects: {
      total_completed: data.projects.totalCompleted,
      total_in_progress: data.projects.totalInProgress,
      total_scheduled: data.projects.totalScheduled,
      count_completed: data.projects.countCompleted,
      count_in_progress: data.projects.countInProgress,
      count_scheduled: data.projects.countScheduled,
    },
  };
}

function transformPaymentAging(data: ApiPaymentAging): PaymentAging {
  return {
    current: data.current,
    days_1_to_30: data.days1to30,
    days_31_to_60: data.days31to60,
    days_61_to_90: data.days61to90,
    days_90_plus: data.days90Plus,
  };
}

function transformRevenueByType(data: ApiRevenueByType): RevenueByType {
  return {
    type: data.type,
    total_amount: data.totalAmount,
    completed_amount: data.completedAmount,
    total_count: data.totalCount,
    completed_count: data.completedCount,
  };
}

function transformMonthlyRevenue(data: ApiMonthlyRevenue): MonthlyRevenue {
  return {
    month: data.month,
    invoice_revenue: data.invoiceRevenue,
    project_revenue: data.projectRevenue,
    invoice_count: data.invoiceCount,
    project_count: data.projectCount,
  };
}

function transformTopAccount(data: ApiTopAccount): TopAccount {
  return {
    account_id: data.accountId,
    account_number: data.accountNumber,
    account_name: data.accountName,
    total_paid: data.totalPaid,
    total_pending: data.totalPending,
    invoice_count: data.invoiceCount,
  };
}

function transformTopProject(data: ApiTopProject): TopProject {
  return {
    id: data.id,
    project_number: data.projectNumber,
    project_name: data.projectName,
    type: data.type,
    status: data.status,
    amount: data.amount,
    quote_number: data.quoteNumber,
    scheduled_date: data.scheduledDate,
    completed_at: data.completedAt,
  };
}

function transformQuoteFunnel(data: ApiQuoteConversionFunnel): QuoteConversionFunnel {
  return {
    total: data.total,
    stages: data.stages,
    values: data.values,
    conversion_rate: data.conversionRate,
    win_rate: data.winRate,
  };
}

function transformQuotesPipelineSummary(data: ApiQuotesPipelineSummary): QuotesPipelineSummary {
  return {
    buckets: data.buckets,
    total_quotes: data.totalQuotes,
    total_value: data.totalValue,
  };
}

function transformQuotePipelineItem(data: ApiQuotePipelineItem): QuotePipelineItem {
  return {
    id: data.id,
    quote_number: data.quoteNumber,
    project_name: data.projectName ?? null,
    po_number: data.poNumber ?? null,
    status: data.status,
    total: data.total,
    currency: data.currency,
    account_id: data.accountId,
    account_name: data.accountName,
    account_number: data.accountNumber,
    estimated_delivery: data.estimatedDelivery,
    created_date: data.createdDate,
    sent_date: data.sentDate,
    accepted_date: data.acceptedDate,
    contractual_manager_id: data.contractualManagerId,
    invoiced_date: data.invoicedDate,
  };
}

function transformQuotesPipelineResponse(data: ApiQuotesPipelineResponse): QuotesPipelineResponse {
  return {
    quotes: data.quotes.map(transformQuotePipelineItem),
    page_number: data.pageNumber,
    page_length: data.pageLength,
    total: data.total,
  };
}

// API Functions

export async function fetchFinancialSummary(): Promise<FinancialSummary> {
  const data = await api.get<ApiFinancialSummary>('/v4/operations-financial/summary');
  return transformSummary(data);
}

export async function fetchPaymentAging(): Promise<PaymentAging> {
  const data = await api.get<ApiPaymentAging>('/v4/operations-financial/payment-aging');
  return transformPaymentAging(data);
}

export async function fetchRevenueByType(): Promise<RevenueByType[]> {
  const data = await api.get<ApiRevenueByType[]>('/v4/operations-financial/revenue-by-type');
  return data.map(transformRevenueByType);
}

export async function fetchRevenueByPeriod(months: number = 12): Promise<MonthlyRevenue[]> {
  const data = await api.get<ApiMonthlyRevenue[]>('/v4/operations-financial/revenue-by-period', { months });
  return data.map(transformMonthlyRevenue);
}

export async function fetchTopAccounts(limit: number = 10): Promise<TopAccount[]> {
  const data = await api.get<ApiTopAccount[]>('/v4/operations-financial/top-accounts', { limit });
  return data.map(transformTopAccount);
}

export async function fetchTopProjects(limit: number = 10, status?: string): Promise<TopProject[]> {
  const params: Record<string, unknown> = { limit };
  if (status) params.status = status;
  const data = await api.get<ApiTopProject[]>('/v4/operations-financial/top-projects', params);
  return data.map(transformTopProject);
}

export async function fetchQuoteFunnel(months: number = 6): Promise<QuoteConversionFunnel> {
  const data = await api.get<ApiQuoteConversionFunnel>('/v4/operations-financial/quote-funnel', { months });
  return transformQuoteFunnel(data);
}

export async function fetchQuotesPipelineSummary(params?: {
  estimated_delivery_from?: string;
  estimated_delivery_to?: string;
  created_date_from?: string;
  created_date_to?: string;
  invoiced_date_from?: string;
  invoiced_date_to?: string;
}): Promise<QuotesPipelineSummary> {
  const queryParams: Record<string, string> = {};
  if (params?.estimated_delivery_from) queryParams.estimatedDeliveryFrom = params.estimated_delivery_from;
  if (params?.estimated_delivery_to) queryParams.estimatedDeliveryTo = params.estimated_delivery_to;
  // #890 — keep the summary in lockstep with the list so bucket counts reflect the filtered board.
  if (params?.created_date_from) queryParams.createdDateFrom = params.created_date_from;
  if (params?.created_date_to) queryParams.createdDateTo = params.created_date_to;
  if (params?.invoiced_date_from) queryParams.invoicedDateFrom = params.invoiced_date_from;
  if (params?.invoiced_date_to) queryParams.invoicedDateTo = params.invoiced_date_to;
  const data = await api.get<ApiQuotesPipelineSummary>('/v4/operations-financial/quotes-pipeline-summary', queryParams);
  return transformQuotesPipelineSummary(data);
}

export async function fetchQuotesPipeline(params?: QuotesPipelineQueryParams): Promise<QuotesPipelineResponse> {
  const queryParams: Record<string, unknown> = {};
  if (params?.status_in?.length) queryParams.statusIn = JSON.stringify(params.status_in);
  if (params?.estimated_delivery_from) queryParams.estimatedDeliveryFrom = params.estimated_delivery_from;
  if (params?.estimated_delivery_to) queryParams.estimatedDeliveryTo = params.estimated_delivery_to;
  if (params?.account_id) queryParams.accountId = params.account_id;
  if (params?.sales_manager_id) queryParams.salesManagerId = params.sales_manager_id;
  if (params?.created_date_from) queryParams.createdDateFrom = params.created_date_from;
  if (params?.created_date_to) queryParams.createdDateTo = params.created_date_to;
  if (params?.invoiced_date_from) queryParams.invoicedDateFrom = params.invoiced_date_from;
  if (params?.invoiced_date_to) queryParams.invoicedDateTo = params.invoiced_date_to;
  if (params?.po_number) queryParams.poNumber = params.po_number;
  if (params?.page_number !== undefined) queryParams.pageNumber = params.page_number;
  if (params?.page_length !== undefined) queryParams.pageLength = params.page_length;
  if (params?.sort_key) queryParams.sortKey = params.sort_key;
  if (params?.sort_direction !== undefined) queryParams.sortDirection = params.sort_direction;
  const data = await api.get<ApiQuotesPipelineResponse>('/v4/operations-financial/quotes-pipeline', queryParams);
  return transformQuotesPipelineResponse(data);
}
